import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import {isMobile} from '@insight/toolkit-utils'
/**
 * FilterTooltip Component
 * A customizable tooltip with header and body sections that can contain HTML elements.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.header - Content for the tooltip header (can be string or JSX)
 * @param {React.ReactNode} props.body - Content for the tooltip body (can be string or JSX)
 * @param {React.ReactNode} props.trigger - Element that triggers the tooltip
 * @param {string} props.position - Tooltip position: 'top', 'bottom', 'left', 'right' (default: 'right')
 * @param {string} props.className - Additional CSS classes for the tooltip container
 * @param {number} props.showDelay - Delay in ms before showing tooltip (default: 200)
 * @param {number} props.hideDelay - Delay in ms before hiding tooltip (default: 200)
 * @param {boolean} props.hoverable - Whether the tooltip body stays visible on hover (default: true)
 * @param {string} props.triggerOn - Trigger event: 'hover', 'click', 'both' (default: 'hover')
 */
export const FilterTooltip = ({
  header,
  body,
  trigger,
  position = 'right',
  className = '',
  showDelay = 200,
  hideDelay = 200,
  hoverable = true,
  triggerOn = 'hover'
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false)
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false)
  const [calculatedPosition, setCalculatedPosition] = useState(position)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const showTimeoutRef = useRef(null)
  const hideTimeoutRef = useRef(null)
  const tooltipRef = useRef(null)
  const triggerRef = useRef(null)
  const containerRef = useRef(null)

  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current)
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    }
  }, [])

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isVisible &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsVisible(false)
        setIsHoveringTooltip(false)
        setIsHoveringTrigger(false)
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isVisible])

  // Calculate optimal position based on viewport
  useEffect(() => {
    if (!isVisible || !tooltipRef.current || !triggerRef.current) {
      return
    }

    const calculatePosition = () => {
      if (!tooltipRef.current || !triggerRef.current) return

      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const padding = 16 // Padding from viewport edges
      const arrowSize = 8 // Arrow size to account for

      // Calculate available space in each direction
      const spaceTop = triggerRect.top
      const spaceBottom = viewportHeight - triggerRect.bottom
      const spaceLeft = triggerRect.left
      const spaceRight = viewportWidth - triggerRect.right

      // Adjust tooltip width based on viewport size and available space
      const maxWidth = 450
      const minWidth = 200
      let dynamicWidth = maxWidth
      
      // For small screens, reduce width
      // adjust the modal's width to be fit with mobile's filters width
      // The 110px offset mirrors the extra fixed space within the mobile Filters modal.
      if (viewportWidth < 768) {
        dynamicWidth = Math.min(maxWidth, viewportWidth - padding * 2) - 110
      } else if (viewportWidth < 1200) {
        dynamicWidth = 450
      }

      // Calculate required space (tooltip size + arrow + padding)
      const requiredVerticalSpace = tooltipRect.height + arrowSize + padding
      const requiredHorizontalSpace = dynamicWidth + arrowSize + padding

      // Check if there's enough space in each direction
      const fits = {
        top: spaceTop >= requiredVerticalSpace,
        bottom: spaceBottom >= requiredVerticalSpace,
        left: spaceLeft >= requiredHorizontalSpace,
        right: spaceRight >= requiredHorizontalSpace
      }

      let newPosition = position

      // Smart positioning: Consider both the preferred position and vertical constraints
      // If we're near the top or bottom of viewport, prioritize vertical positioning
      const isNearTop = triggerRect.top < viewportHeight * 0.3
      const isNearBottom = triggerRect.bottom > viewportHeight * 0.7

      if (position === 'right' || position === 'left') {
        // For horizontal positions, check if we should switch to vertical
        if (isNearTop && !fits.bottom) {
          // Near top with no bottom space - don't show or use top if available
          newPosition = fits.top ? 'top' : position
        } else if (isNearBottom && !fits.top) {
          // Near bottom with no top space - don't show or use bottom if available
          newPosition = fits.bottom ? 'bottom' : position
        } else if (isNearTop && fits.bottom) {
          // Near top with bottom space available - prefer bottom
          newPosition = 'bottom'
        } else if (isNearBottom && fits.top) {
          // Near bottom with top space available - prefer top
          newPosition = 'top'
        } else if (position === 'right' && !fits.right) {
          // Preferred right doesn't fit
          if (fits.left) newPosition = 'left'
          else if (fits.bottom) newPosition = 'bottom'
          else if (fits.top) newPosition = 'top'
        } else if (position === 'left' && !fits.left) {
          // Preferred left doesn't fit
          if (fits.right) newPosition = 'right'
          else if (fits.bottom) newPosition = 'bottom'
          else if (fits.top) newPosition = 'top'
        }
      } else if (position === 'top') {
        if (!fits.top) {
          if (fits.bottom) newPosition = 'bottom'
          else if (fits.right && spaceRight > spaceLeft) newPosition = 'right'
          else if (fits.left) newPosition = 'left'
          else newPosition = 'bottom'
        }
      } else if (position === 'bottom') {
        if (!fits.bottom) {
          if (fits.top) newPosition = 'top'
          else if (fits.right && spaceRight > spaceLeft) newPosition = 'right'
          else if (fits.left) newPosition = 'left'
          else newPosition = 'top'
        }
      }

      // Calculate positioning adjustments to keep tooltip in viewport
      let style = { width: `${dynamicWidth}px` }

      // For horizontal positions (left/right), adjust vertical alignment if needed
      if (newPosition === 'left' || newPosition === 'right') {
        const tooltipHalfHeight = tooltipRect.height / 2
        const triggerCenter = triggerRect.top + (triggerRect.height / 2)
        
        // Check if tooltip would go above viewport
        if (triggerCenter - tooltipHalfHeight < padding) {
          style.top = `${padding - triggerRect.top}px`
          style.transform = 'translateY(0)'
        }
        // Check if tooltip would go below viewport
        else if (triggerCenter + tooltipHalfHeight > viewportHeight - padding) {
          style.bottom = `${padding}px`
          style.top = 'auto'
          style.transform = 'translateY(0)'
        }
      }

      // For vertical positions (top/bottom), adjust horizontal alignment if needed
      if (newPosition === 'top' || newPosition === 'bottom') {
        const tooltipHalfWidth = dynamicWidth / 2
        const triggerCenter = triggerRect.left + (triggerRect.width / 2)
        
        // Check if tooltip would go off left edge
        if (triggerCenter - tooltipHalfWidth < padding) {
          style.left = window.innerWidth < 1200 ? 0 : `${padding - triggerRect.left}px`
          style.transform = 'translateX(0)'
        }
        // Check if tooltip would go off right edge
        else if (triggerCenter + tooltipHalfWidth > viewportWidth - padding) {
          style.right = `${padding}px`
          style.left = 'auto'
          style.transform = 'translateX(0)'
        }
      }

      setCalculatedPosition(newPosition)
      setTooltipStyle(style)
    }

    // Initial calculation with multiple attempts to ensure tooltip is rendered
    const timeoutId1 = setTimeout(calculatePosition, 0)
    const timeoutId2 = setTimeout(calculatePosition, 50)
    const timeoutId3 = setTimeout(calculatePosition, 100)

    // Recalculate on scroll or resize
    const scrollHandler = () => {
      requestAnimationFrame(calculatePosition)
    }

    window.addEventListener('scroll', scrollHandler, true)
    window.addEventListener('resize', scrollHandler)

    return () => {
      clearTimeout(timeoutId1)
      clearTimeout(timeoutId2)
      clearTimeout(timeoutId3)
      window.removeEventListener('scroll', scrollHandler, true)
      window.removeEventListener('resize', scrollHandler)
    }
  }, [isVisible, position])

  // Handle visibility based on hover state
  useEffect(() => {
    if (!hoverable) return

    if (isHoveringTooltip) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
      setIsVisible(true)
    } else if (!isHoveringTrigger && !isHoveringTooltip) {
      // If neither trigger nor tooltip is being hovered, hide it
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, hideDelay)
    }
  }, [isHoveringTooltip, isHoveringTrigger, hoverable, hideDelay])

  const handleShow = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true)
    }, showDelay)
  }

  const handleHide = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }

    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip || !hoverable) {
        setIsVisible(false)
        setIsHoveringTrigger(false)
      }
    }, hideDelay)
  }

  const handleTriggerClick = () => {
    if (isMobile()) {
      setIsVisible(!isVisible)
    }
    if (!isMobile() && (triggerOn === 'click' || triggerOn === 'both')) {
      setIsVisible(!isVisible)
    }
  }

  const handleTriggerMouseEnter = () => {
    setIsHoveringTrigger(true)
    handleShow()
  }

  const handleTriggerMouseLeave = () => {
    setIsHoveringTrigger(false)
    handleHide()
  }

  const handleTooltipMouseEnter = () => {
    if (hoverable) {
      setIsHoveringTooltip(true)
    }
  }

  const handleTooltipMouseLeave = () => {
    if (hoverable) {
      setIsHoveringTooltip(false)
    }
  }

  const shouldTriggerOnHover = triggerOn === 'hover' || triggerOn === 'both'

  return (
    <div ref={containerRef} className={cn('c-filter-tooltip', className)}>
      <div
        ref={triggerRef}
        className="c-filter-tooltip__trigger"
        onMouseEnter={shouldTriggerOnHover ? handleTriggerMouseEnter : undefined}
        onMouseLeave={shouldTriggerOnHover ? handleTriggerMouseLeave : undefined}
        onClick={handleTriggerClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleTriggerClick()
          }
        }}
        aria-describedby={isVisible ? 'filter-tooltip-content' : undefined}
      >
        {trigger}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          id="filter-tooltip-content"
          className={cn(
            'c-filter-tooltip__content',
            `c-filter-tooltip__content--${calculatedPosition}`
          )}
          style={tooltipStyle}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          role="tooltip"
        >
          {header && (
            <div className="c-filter-tooltip__header">
              {header}
            </div>
          )}
          {body && (
            <div className="c-filter-tooltip__body">
              {body}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

FilterTooltip.propTypes = {
  header: PropTypes.node,
  body: PropTypes.node,
  trigger: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  className: PropTypes.string,
  showDelay: PropTypes.number,
  hideDelay: PropTypes.number,
  hoverable: PropTypes.bool,
  triggerOn: PropTypes.oneOf(['hover', 'click', 'both'])
}

export default FilterTooltip
